import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import type { DashboardResponse } from '../services/dashboardService';
import { useNavigate } from 'react-router-dom';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import WarningIcon from '@mui/icons-material/Warning';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type Range = 0 | 7 | 30;

const RANGES: { value: Range; label: string }[] = [
  { value: 0, label: '24H' },
  { value: 7, label: '7D' },
  { value: 30, label: '30D' },
];

const formatINR = (v: number) =>
  '₹' + v.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const formatDateTime = (dt: string | null | undefined): string => {
  if (!dt) return '—';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return String(dt);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { precision: 0, font: { size: 9 }, color: '#434654' },
      grid: { color: '#E2E8F0' },
    },
    x: {
      ticks: { font: { size: 9 }, color: '#434654' },
      grid: { display: false },
    },
  },
};

const Dashboard: React.FC = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>(0);
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.dashboard.get(range, { signal: controller.signal });
        setData(res);
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load dashboard');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [api, range, reloadKey]);

  return (
    <div className="space-y-4 px-4 mt-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 gap-4">
        <div>
          <h1 className="font-headline-lg font-bold text-on-surface">Overview Dashboard</h1>
          <p className="text-on-surface-variant font-body-sm mt-0.5">
            Real-time market intelligence and pricing analytics.
          </p>
        </div>
        <div className="flex gap-2 w-full md:w-auto flex-wrap">
          <div className="flex items-center gap-0.5 bg-white border border-border-subtle rounded-lg p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => {
                  setRange(r.value);
                  setReloadKey((k) => k + 1);
                }}
                aria-pressed={range === r.value}
                className={`px-3 py-1.5 rounded-md text-sm font-medium flex-1 md:flex-none transition-colors ${
                  range === r.value ? 'bg-primary text-white' : 'text-on-surface hover:bg-surface-container-low'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={loading}
            className="flex-1 md:flex-none px-3 py-1.5 bg-white border border-border-subtle rounded-lg text-sm text-on-surface font-medium hover:bg-surface-container-low flex items-center justify-center gap-1 disabled:opacity-60"
          >
            <RefreshIcon className={`text-[16px] ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            onClick={() => navigate('/comparison')}
            className="flex-1 md:flex-none px-3 py-1.5 bg-white border border-border-subtle rounded-lg text-sm text-on-surface font-medium hover:bg-surface-container-low flex items-center justify-center gap-1"
          >
            <FilterListIcon className="text-[16px]" /> Filter
          </button>
        </div>
      </div>

      {error ? (
        <div className="bg-white border border-border-subtle rounded-lg p-8 flex flex-col items-center justify-center gap-3 text-center">
          <WarningIcon className="text-warning text-[32px]" />
          <p className="font-body-sm text-on-surface">Couldn't load dashboard data</p>
          <p className="text-xs text-on-surface-variant max-w-md">{error}</p>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-container hover:text-on-primary transition-colors disabled:opacity-60 flex items-center gap-1"
          >
            <RefreshIcon className={`text-[16px] ${loading ? 'animate-spin' : ''}`} /> Retry
          </button>
        </div>
      ) : (
        <>
          {/* Bento Metric Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {/* Avg Market Position */}
            <div className="bg-white p-4 md:p-5 rounded-lg border border-border-subtle hover:shadow-md transition-all relative overflow-hidden flex flex-col justify-between h-28 md:h-32">
              <div className="flex justify-between items-start z-10">
                <span className="font-label-caps text-[10px] md:text-[11px] text-on-surface-variant uppercase">Avg Market Position</span>
                <LeaderboardIcon className="text-primary text-[18px] md:text-[20px]" />
              </div>
              <div className="z-10">
                <div className="flex items-baseline gap-2">
                  <span className="font-headline-lg text-[20px] md:text-[24px] text-on-surface">
                    {loading ? '—' : (data?.metrics.avgMarketPosition ?? 0).toFixed(1)}
                  </span>
                </div>
                <p className="font-body-sm text-[11px] text-on-surface-variant mt-0.5">
                  Across {loading ? '—' : (data?.metrics.totalSKUs ?? 0).toLocaleString()} active SKUs
                </p>
              </div>
            </div>

            {/* Pricing Alerts */}
            <div className="bg-white p-4 md:p-5 rounded-lg border border-border-subtle hover:shadow-md transition-all relative overflow-hidden flex flex-col justify-between h-28 md:h-32">
              <div className="flex justify-between items-start z-10">
                <span className="font-label-caps text-[10px] md:text-[11px] text-on-surface-variant uppercase">Pricing Alerts (Overpriced)</span>
                <WarningIcon className="text-warning text-[18px] md:text-[20px]" />
              </div>
              <div className="z-10">
                <div className="flex items-baseline gap-2">
                  <span className="font-headline-lg text-[20px] md:text-[24px] text-on-surface">
                    {loading ? '—' : (data?.metrics.overpricedCount ?? 0).toLocaleString()}
                  </span>
                </div>
                <p className="font-body-sm text-[11px] text-on-surface-variant mt-0.5">Products above lowest price</p>
              </div>
            </div>

            {/* Stock Health */}
            <div className="bg-white p-4 md:p-5 rounded-lg border border-border-subtle hover:shadow-md transition-all relative overflow-hidden flex flex-col justify-between h-28 md:h-32">
              <div className="flex justify-between items-start z-10">
                <span className="font-label-caps text-[10px] md:text-[11px] text-on-surface-variant uppercase">Stock Health Index</span>
                <Inventory2Icon className="text-success text-[18px] md:text-[20px]" />
              </div>
              <div className="z-10">
                <div className="flex items-baseline gap-2">
                  <span className="font-headline-lg text-[20px] md:text-[24px] text-on-surface">
                    {loading ? '—' : `${(data?.metrics.inStockPct ?? 0).toFixed(1)}%`}
                  </span>
                </div>
                <div className="w-full bg-surface-container-high h-1.5 rounded-full mt-2">
                  <div
                    className="bg-success h-1.5 rounded-full transition-all duration-500"
                    style={{ width: loading ? '0%' : `${Math.min(data?.metrics.inStockPct ?? 0, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Category + Brand Composition */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Category Composition */}
            <div className="bg-white border border-border-subtle rounded-lg flex flex-col">
              <div className="p-3 md:p-4 border-b border-border-subtle bg-surface-container-low rounded-t-lg">
                <h3 className="font-headline-sm text-[16px] text-on-surface">Category Composition</h3>
              </div>
              <div className="p-4">
                {loading ? (
                  <div className="flex items-center justify-center h-48">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (data?.categoryComposition ?? []).length === 0 ? (
                  <div className="flex items-center justify-center h-48 text-on-surface-variant text-body-sm">No category data</div>
                ) : (
                  <div className="h-48 md:h-56">
                    <Bar
                      data={{
                        labels: (data?.categoryComposition ?? []).map((c) => c.category),
                        datasets: [
                          {
                            label: 'SKUs',
                            data: (data?.categoryComposition ?? []).map((c) => c.count),
                            backgroundColor: '#003D9B',
                            borderRadius: 4,
                          },
                        ],
                      }}
                      options={chartOptions}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Brand Composition */}
            <div className="bg-white border border-border-subtle rounded-lg flex flex-col">
              <div className="p-3 md:p-4 border-b border-border-subtle bg-surface-container-low rounded-t-lg">
                <h3 className="font-headline-sm text-[16px] text-on-surface">Brand Composition</h3>
              </div>
              <div className="p-4">
                {loading ? (
                  <div className="flex items-center justify-center h-48">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (data?.brandComposition ?? []).length === 0 ? (
                  <div className="flex items-center justify-center h-48 text-on-surface-variant text-body-sm">No brand data</div>
                ) : (
                  <div className="h-48 md:h-56">
                    <Bar
                      data={{
                        labels: (data?.brandComposition ?? []).map((b) => b.brand),
                        datasets: [
                          {
                            label: 'SKUs',
                            data: (data?.brandComposition ?? []).map((b) => b.count),
                            backgroundColor: '#22C55E',
                            borderRadius: 4,
                          },
                        ],
                      }}
                      options={chartOptions}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            {/* Top Buy-Box Opportunities */}
            <div className="lg:col-span-2 bg-white border border-border-subtle rounded-lg flex flex-col">
              <div className="p-3 md:p-4 border-b border-border-subtle flex justify-between items-center bg-surface-container-low rounded-t-lg">
                <h3 className="font-headline-sm text-[16px] text-on-surface">Top Buy-Box Opportunities</h3>
                <button
                  onClick={() => navigate('/comparison')}
                  className="text-primary text-[13px] font-medium hover:underline"
                >
                  View All
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (data?.topOpportunities ?? []).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-on-surface-variant">
                  <CheckCircleIcon className="text-[32px] text-success mb-2" />
                  <p className="text-body-sm">All products are competitively priced</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-border-subtle font-label-caps text-[11px] text-on-surface-variant uppercase bg-surface-container-low">
                          <th className="py-2 px-3">SKU / Product</th>
                          <th className="py-2 px-3 text-right">Our Price</th>
                          <th className="py-2 px-3 text-right">Competitor Lowest</th>
                          <th className="py-2 px-3 text-right">Target Drop</th>
                          <th className="py-2 px-3 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="font-body-sm text-[13px]">
                        {(data?.topOpportunities ?? []).map((o) => (
                          <tr key={o.product_id} className="border-b border-border-subtle last:border-0 hover:bg-surface-container-lowest transition-colors group">
                            <td className="py-2.5 px-3">
                              <div className="font-medium text-on-surface">{o.sku}</div>
                              <div className="text-on-surface-variant text-xs truncate max-w-[200px]">{o.title}</div>
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono text-on-surface">{formatINR(o.md_price)}</td>
                            <td className="py-2.5 px-3 text-right">
                              <span className="font-mono text-on-surface-variant">{formatINR(o.lowest_price)}</span>
                              <div className="text-[10px] text-on-surface-variant/70">{o.lowest_competitor}</div>
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono text-success">-{formatINR(o.target_drop)}</td>
                            <td className="py-2.5 px-3 text-center">
                              <button
                                onClick={() => navigate('/comparison')}
                                className="px-2 py-1 bg-primary-container text-on-primary rounded text-xs font-medium hover:bg-primary opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                              >
                                Match
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card List */}
                  <div className="md:hidden flex flex-col">
                    {(data?.topOpportunities ?? []).map((o, idx) => (
                      <div key={o.product_id} className={`p-4 border-b border-border-subtle ${idx % 2 === 1 ? 'bg-surface-container-low/30' : 'bg-white'}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-medium text-[14px] text-on-surface">{o.sku}</div>
                            <div className="text-on-surface-variant text-[12px] truncate max-w-[200px]">{o.title}</div>
                          </div>
                          <button
                            onClick={() => navigate('/comparison')}
                            className="px-3 py-1.5 bg-primary-container text-on-primary rounded text-[12px] font-medium hover:bg-primary active:scale-95 transition-all"
                          >
                            Match
                          </button>
                        </div>
                        <div className="flex justify-between items-center text-[13px] font-mono mt-3">
                          <div>
                            <span className="text-on-surface-variant block text-[10px] uppercase tracking-wide">Our Price</span>
                            <span className="text-on-surface">{formatINR(o.md_price)}</span>
                          </div>
                          <div>
                            <span className="text-on-surface-variant block text-[10px] uppercase tracking-wide">Lowest</span>
                            <span className="text-on-surface-variant">{formatINR(o.lowest_price)}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-on-surface-variant block text-[10px] uppercase tracking-wide">Target Drop</span>
                            <span className="text-success font-bold">-{formatINR(o.target_drop)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Recent Price Shifts */}
            <div className="bg-white border border-border-subtle rounded-lg flex flex-col h-[350px] md:h-[400px]">
              <div className="p-3 md:p-4 border-b border-border-subtle bg-surface-container-low rounded-t-lg">
                <h3 className="font-headline-sm text-[16px] text-on-surface">Recent Price Shifts</h3>
              </div>
              <div className="p-4 overflow-y-auto flex-1 space-y-5">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (data?.recentShifts ?? []).length === 0 ? (
                  <p className="text-center text-on-surface-variant text-body-sm py-8">No recent price shifts</p>
                ) : (data?.recentShifts ?? []).map((s) => (
                  <div key={s.id} className="flex gap-3 items-start">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${s.direction === 'down' ? 'bg-danger' : 'bg-success'}`} />
                    <div>
                      <p className="font-body-sm text-[13px] text-on-surface leading-snug">
                        <span className="font-semibold">{s.company}</span>{' '}
                        {s.direction === 'down' ? 'dropped price on' : 'changed price on'}{' '}
                        <span className="font-mono text-[11px] bg-surface-container px-1 py-0.5 rounded text-primary">
                          {s.sku}
                        </span>
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="font-mono text-xs text-on-surface-variant line-through">{formatINR(s.old_price)}</span>
                        <ArrowForwardIcon className="text-[12px] text-outline" />
                        <span className={`font-mono text-xs font-semibold ${s.direction === 'down' ? 'text-danger' : 'text-success'}`}>
                          {formatINR(s.new_price)}
                        </span>
                      </div>
                      <span className="text-[10px] text-on-surface-variant mt-1.5 block">{formatDateTime(s.dt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Crawler Health + Top Movers + Out-of-Stock Alerts */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Crawler Health */}
            <div className="bg-white border border-border-subtle rounded-lg flex flex-col h-[350px] md:h-[400px]">
              <div className="p-3 md:p-4 border-b border-border-subtle bg-surface-container-low rounded-t-lg">
                <h3 className="font-headline-sm text-[16px] text-on-surface">Crawler Health</h3>
              </div>
              <div className="p-4 overflow-y-auto flex-1 space-y-3">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (data?.crawlerHealth ?? []).length === 0 ? (
                  <p className="text-center text-on-surface-variant text-body-sm py-8">No crawler data</p>
                ) : (
                  (data?.crawlerHealth ?? []).map((h) => (
                    <div key={h.company} className="flex items-center justify-between gap-2">
                      <div>
                        <div className="font-medium text-[13px] text-on-surface">{h.company}</div>
                        <div className="text-[11px] text-on-surface-variant">Last crawl: {formatDateTime(h.lastCrawlDt)}</div>
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0 ${
                          h.errorCount24h > 0 ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success'
                        }`}
                      >
                        {h.errorCount24h > 0 ? `${h.errorCount24h} errors` : 'OK'}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Top Movers */}
            <div className="bg-white border border-border-subtle rounded-lg flex flex-col h-[350px] md:h-[400px]">
              <div className="p-3 md:p-4 border-b border-border-subtle bg-surface-container-low rounded-t-lg">
                <h3 className="font-headline-sm text-[16px] text-on-surface">Top Movers</h3>
              </div>
              <div className="p-4 overflow-y-auto flex-1 space-y-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (data?.topMovers ?? []).length === 0 ? (
                  <p className="text-center text-on-surface-variant text-body-sm py-8">No price moves in this window</p>
                ) : (
                  (data?.topMovers ?? []).map((m) => (
                    <div key={m.id} className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-[13px] text-on-surface truncate">{m.sku}</div>
                        <div className="text-[11px] text-on-surface-variant truncate">{m.title}</div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="text-[11px] text-on-surface-variant">{m.company}</span>
                          <span className="font-mono text-[11px] text-on-surface-variant line-through">{formatINR(m.old_price)}</span>
                          <ArrowForwardIcon className="text-[10px] text-outline" />
                          <span className={`font-mono text-[11px] font-semibold ${m.direction === 'down' ? 'text-danger' : 'text-success'}`}>
                            {formatINR(m.new_price)}
                          </span>
                        </div>
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0 ${
                          m.direction === 'down' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                        }`}
                      >
                        {(m.pct ?? 0).toFixed(1)}%
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Out-of-Stock Alerts */}
            <div className="bg-white border border-border-subtle rounded-lg flex flex-col h-[350px] md:h-[400px]">
              <div className="p-3 md:p-4 border-b border-border-subtle bg-surface-container-low rounded-t-lg">
                <h3 className="font-headline-sm text-[16px] text-on-surface">Out-of-Stock Alerts</h3>
              </div>
              <div className="p-4 overflow-y-auto flex-1 space-y-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (data?.outOfStockAlerts ?? []).length === 0 ? (
                  <p className="text-center text-on-surface-variant text-body-sm py-8">All tracked SKUs are in stock</p>
                ) : (
                  (data?.outOfStockAlerts ?? []).map((o) => (
                    <div key={o.product_id} className="flex gap-3 items-start">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 bg-danger`} />
                      <div>
                        <div className="font-medium text-[13px] text-on-surface">{o.sku}</div>
                        <div className="text-[11px] text-on-surface-variant truncate">{o.title}</div>
                        <div className="text-[11px] text-on-surface-variant mt-1">
                          <span className="font-semibold text-on-surface-variant">MD SKU:</span>{' '}
                          <span className="font-mono">{o.md_sku}</span>
                        </div>
                        <div className="text-[11px] text-on-surface-variant mt-0.5">
                          Available at: {o.availableAt?.length ? o.availableAt.join(', ') : '—'}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;